/* eslint-disable react-hooks/set-state-in-effect -- appraisal step effects synchronize external vehicle data and workflow state. */
// React and Redux
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Third-party libraries
import classnames from 'classnames';
import { isEmpty, forEach, cloneDeep, size, isEqual } from 'lodash-es';

// Utilities
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { EXTERIOR_COLOR } from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { buildStylesBasicPathFromVin, VehicleVinModel } from 'client/data/models/vehicle-vin';
import { transformedVehicleFeaturesPath, PartnerOfferModel } from 'client/data/models/partner-offer-model';
import { fireDecodeMatchColorsAndOptionsPixels } from 'site-modules/shared/utils/appraisal/common-utils';
import { getPrequalifiedPartners } from 'site-modules/shared/utils/multi-offer/multi-offer';
import { useTradeInAppraiserContext } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-context';

// Hooks
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

// Constants
import {
  APPRAISAL_PAGE_CREATIVE_ID,
  DRAWER_CREATIVE_ID,
  getMultiOfferPrequalificationTrackingEvent,
  getMultiOfferPrequalificationIneligibleTrackingEvent,
  PARTNER_OFFER_TRACKING_CREATIVE_ID,
} from 'site-modules/shared/constants/appraisal/appraisal';

// Components
import { FacetCheckbox } from 'site-modules/shared/components/filter-facets/facet-checkbox/facet-checkbox';
import { TransmissionTypeSelection } from './transmission-type-selection';
import { ColorsSection } from './colors-section';

import {
  getColorsWithRgbLength,
  getOptionsToDisplay,
  isLowValueOption,
  isEngineOrTransmissionOption,
  getSelectedOptions,
  isOptionsEqual,
  isPackageSelected,
  getPackageDeselectionIds,
} from './colors-and-options-step-utils';

import './colors-and-options-step.scss';

const SUBACTION_NAMES = {
  AVAILABLE_LOW_VALUE_OPTIONS_COUNT: 'available_low_value_options_count',
  STYLE_ENGINE_SIZE: 'style_engine_size',
  STYLE_TRANSMISSION_TYPE: 'style_transmission_type',
  STYLE_DRIVETRAIN: 'style_drivetrain',
  STYLE_CHROME_STYLE_ID_COUNT: 'style_chrome_style_id_count',
  STYLE_CHROME_STYLE_ID_PASSED: 'style_chrome_style_id_passed',
  SUBMIT_PACKAGES: TrackingConstant.SUBMIT_PACKAGES,
};

const UNDEFINED_STR_VALUE = 'undefined';

const getTransmissionTypeSubOption = subOption => subOption?.transmission?.transmissionType;

const getEngineSizeSubOption = subOption => subOption?.engine?.size;

const findColorByName = (colors, colorName) => colors.find(({ name }) => name === colorName);

/**
 * Fires tracking events for multi-offer prequalification.
 * @param {Object} multiOfferPrequalification
 */
const fireMultiOfferPrequalificationTracking = multiOfferPrequalification => {
  if (isEmpty(multiOfferPrequalification)) {
    return;
  }

  const { partners } = multiOfferPrequalification;

  forEach(partners, ({ solicitation: { isPrequalified, reason } }, partner) => {
    EventToolbox.fireTrackAction(
      getMultiOfferPrequalificationTrackingEvent(partner, isPrequalified, PARTNER_OFFER_TRACKING_CREATIVE_ID)
    );

    if (!isPrequalified) {
      EventToolbox.fireTrackAction(
        getMultiOfferPrequalificationIneligibleTrackingEvent(partner, reason, PARTNER_OFFER_TRACKING_CREATIVE_ID)
      );
    }
  });

  const prequalifiedPartners = getPrequalifiedPartners(multiOfferPrequalification);
  const prequalifiedPartnersStr = prequalifiedPartners.join(',');
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      action_cause: TrackingConstant.SYSTEM_ACTION_CAUSE,
      action_name: TrackingConstant.ACTION_RECEIVE_EPO,
      subaction_name: TrackingConstant.EMO_PARTNER_TO_SOLICIT,
      creative_id: PARTNER_OFFER_TRACKING_CREATIVE_ID,
      value: prequalifiedPartnersStr || null,
    },
  });
};

/**
 * Gets the lowest priced option from a list.
 * @param {string} optionName
 * @param {string} subOption
 * @param {string} subOptionProp
 * @param {Array} optionList
 * @param {Object} optionInitialValue
 * @param {boolean} returnParentOption
 * @returns {Object}
 */
const getLowestPricedOption = (
  optionName,
  subOption,
  subOptionProp,
  optionList,
  optionInitialValue,
  returnParentOption = false
) =>
  optionList.reduce(
    ({ [optionName]: accOption, price: accPrice }, { options }) => {
      const { [optionName]: reducedOption, price: reducedOptPrice } = options.reduce(
        ({ [optionName]: optAccOption, price: optAccPrice }, individualOption) => {
          const {
            [subOption]: { [subOptionProp]: option },
            price: { baseMSRP },
          } = individualOption;
          if (baseMSRP > 0 && baseMSRP < optAccPrice) {
            return returnParentOption
              ? { [optionName]: individualOption[subOption], price: baseMSRP }
              : { [optionName]: option, price: baseMSRP };
          }
          return { [optionName]: optAccOption, price: optAccPrice };
        },
        { [optionName]: accOption, price: accPrice }
      );
      if (reducedOptPrice < accPrice) {
        return { [optionName]: reducedOption, price: reducedOptPrice };
      }
      return { [optionName]: accOption, price: accPrice };
    },
    { [optionName]: optionInitialValue, price: Infinity }
  )[optionName];

/**
 * Gets selected options with their sub-options.
 * @param {Array} selectedOptions
 * @param {Function} getSubOption
 * @returns {Array}
 */
const getSelectedOptionsWithSubOption = (selectedOptions, getSubOption) =>
  selectedOptions
    .map(option => {
      const { options } = option;
      const filteredOptions = options
        .map(subOption => {
          const optionValue = getSubOption(subOption);
          return optionValue ? subOption : null;
        })
        .filter(subOption => subOption);
      return filteredOptions.length ? { ...option, options: filteredOptions } : null;
    })
    .filter(selectedOption => selectedOption);

export const ColorsAndOptionsStepUI = ({
  vehicleFeatures = undefined,
  prefilledExteriorColorId = null,
  prefilledInteriorColorId = null,
  prefilledOptions = null,
  isStepActive,
  isExtendedReport = false,
  multiOfferPrequalification = {},
  transmissionType: transmissionTypeFromLastQuote = '',
  drawerCreativeId = '',
  errorMap = new Map(),
  isMobile = false,
  isContrastUpdate = false,
  showColors = true,
  showOptions = true,
  isStyleMismatchModal = false,
  isLocationStepActive = false,
  onColorsAndOptionsLoaded = null,
  vinStyles = undefined,
  isStepDone: isStepFinished = false,
  isLoadingColorsAndOptions = false,
  exteriorColor = null,
  interiorColor = null,
}) => {
  const {
    selectOptions: onOptionsSelect,
    selectExteriorColor: onExteriorColorSelect,
    selectInteriorColor: onInteriorColorSelect,
    setAdditionalStepsData: updateStepsState,
    setLoadingColorsAndOptions,
    setTranslationTransmissionType,
    setTranslationEngine,
    setChromeStyleIds,
    setHasExteriorColorsOptions,
    resetOnColorsAndOptionsLoaded,
  } = useTradeInAppraiserContext();
  const {
    options: vehicleFeaturesOptions = [],
    packagesToDisplay = [],
    featureToPackages = {},
    pkgByOemName = {},
    engine: vehicleFeaturesEngine = null,
    transmissionType: vehicleFeaturesTransmissionType = null,
    driveTrain: vehicleFeaturesDriveTrain,
    exteriorColors = [],
    interiorColors = [],
    partnerMapping = {},
  } = vehicleFeatures || {};

  const vehicleVinStylesTransmissionType = vinStyles?.transmissionType;

  const [options, setOptions] = useState(vehicleFeaturesOptions);
  const [transmissionType, setTransmissionType] = useState(vehicleFeaturesTransmissionType);
  const [engineOrTransmissionChanged, setEngineOrTransmissionChanged] = useState(false);

  const prevIsStepActive = usePrevious(isStepActive);
  const exteriorColorsSize = size(exteriorColors);
  const prevVehicleFeatures = usePrevious(vehicleFeatures);
  const vehicleFeaturesId = vehicleFeatures?.responseId;
  const hasVehicleFeaturesChanged = vehicleFeatures?.responseId !== prevVehicleFeatures?.responseId;
  const isNotAvailableTransmissionType =
    (!vehicleFeaturesTransmissionType && !vehicleVinStylesTransmissionType) || transmissionType === UNDEFINED_STR_VALUE;
  const optionsToDisplay = getOptionsToDisplay(options, isExtendedReport, showOptions);
  const optionsLength = optionsToDisplay.length;
  const selectedOptions = getSelectedOptions(options);

  const prevMultiOfferPrequalification = usePrevious(multiOfferPrequalification);
  const isStepBecameActive = isStepActive && !prevIsStepActive;
  const isStepDeactivated = !isStepActive && prevIsStepActive;

  const creativeId =
    isExtendedReport && !isStyleMismatchModal ? APPRAISAL_PAGE_CREATIVE_ID : drawerCreativeId || DRAWER_CREATIVE_ID;

  const fireTrackAction = useCallback(
    ({ subactionName, value }) => {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          subaction_name: subactionName,
          creative_id: creativeId,
          value,
        },
      });
    },
    [creativeId]
  );

  useEffect(() => {
    if (isStepFinished && isStepDeactivated && !isLocationStepActive && !isStyleMismatchModal) {
      fireTrackAction({
        subactionName: SUBACTION_NAMES.SUBMIT_PACKAGES,
        value: packagesToDisplay.filter(pkg => isPackageSelected(pkg, options)).length,
      });
    }
  }, [isStepDeactivated, isStepFinished, isLocationStepActive, isStyleMismatchModal]);

  useEffect(() => {
    if (vinStyles && isStepFinished && isStepDeactivated && !isLocationStepActive && !isStyleMismatchModal) {
      fireDecodeMatchColorsAndOptionsPixels({
        vinStylesData: vinStyles,
        selectedExteriorColor: exteriorColor,
        selectedInteriorColor: interiorColor,
        selectedOptions: getSelectedOptions(options),
        creativeId,
      });
    }
    // Use shorter list of dependencies so the hook fires only when users leave step
  }, [isStepFinished, isStepDeactivated, isLocationStepActive, isStyleMismatchModal]);

  // Populate the options again on step 2 when vehicle features change
  useEffect(() => {
    if (hasVehicleFeaturesChanged && !isStepActive && !isStyleMismatchModal) {
      setOptions(vehicleFeaturesOptions);
    }
  }, [hasVehicleFeaturesChanged, isStepActive, isStyleMismatchModal, vehicleFeaturesOptions]);

  const updateEngineAndTransmissionType = useCallback(
    ({ isTransmissionSelectedByUser, newTransmissionType }) => {
      const selectedOptionsWithTransmission = getSelectedOptionsWithSubOption(
        selectedOptions,
        getTransmissionTypeSubOption
      );

      const selectedOptionsWithEngineSize = getSelectedOptionsWithSubOption(selectedOptions, getEngineSizeSubOption);

      const reducedTransmissionType = getLowestPricedOption(
        'transmissionType',
        'transmission',
        'transmissionType',
        selectedOptionsWithTransmission,
        transmissionType
      );

      const reducedEngine = getLowestPricedOption(
        'engineObj',
        'engine',
        'size',
        selectedOptionsWithEngineSize,
        vehicleFeaturesEngine,
        true
      );

      const isEngineOverride = !!selectedOptionsWithEngineSize.length;
      const isTransmissionTypeOverride = !!selectedOptionsWithTransmission.length;
      setTranslationEngine({
        engine: isEngineOverride ? reducedEngine : vehicleFeaturesEngine,
        isOverride: isEngineOverride,
      });

      let predefinedTransmissionType = transmissionType;

      if (!transmissionType && transmissionTypeFromLastQuote && isNotAvailableTransmissionType) {
        predefinedTransmissionType = transmissionTypeFromLastQuote;
      }

      setTranslationTransmissionType({
        transmissionType:
          newTransmissionType ||
          predefinedTransmissionType ||
          (isTransmissionTypeOverride ? reducedTransmissionType : vehicleFeaturesTransmissionType),
        isOverride: isTransmissionTypeOverride,
        isSelectedByUser: isTransmissionSelectedByUser,
      });

      setEngineOrTransmissionChanged(false);
    },
    [
      isNotAvailableTransmissionType,
      selectedOptions,
      setTranslationEngine,
      setTranslationTransmissionType,
      transmissionType,
      transmissionTypeFromLastQuote,
      vehicleFeaturesEngine,
      vehicleFeaturesTransmissionType,
    ]
  );

  useEffect(() => {
    setHasExteriorColorsOptions(!!exteriorColorsSize);
  }, [exteriorColorsSize, setHasExteriorColorsOptions]);

  useEffect(() => {
    if (vehicleFeatures && (hasVehicleFeaturesChanged || isLoadingColorsAndOptions)) {
      setLoadingColorsAndOptions(false);
    }
  }, [hasVehicleFeaturesChanged, setLoadingColorsAndOptions, vehicleFeatures, isLoadingColorsAndOptions]);

  useEffect(() => {
    if (!isLoadingColorsAndOptions && onColorsAndOptionsLoaded) {
      onColorsAndOptionsLoaded();
      resetOnColorsAndOptionsLoaded();
    }
  }, [isLoadingColorsAndOptions, onColorsAndOptionsLoaded, resetOnColorsAndOptionsLoaded]);

  useEffect(() => {
    if (vehicleFeaturesId && isStepActive) {
      // For style mismatch modal those pixels are fired in StyleMismatchModalContentUI component
      if (!isStyleMismatchModal) {
        fireTrackAction({
          subactionName: TrackingConstant.AVAILABLE_EXTERIOR_COLORS_COUNT,
          value: getColorsWithRgbLength(exteriorColors, showColors),
        });
        fireTrackAction({
          subactionName: TrackingConstant.AVAILABLE_INTERIOR_COLORS_COUNT,
          value: getColorsWithRgbLength(interiorColors, showColors),
        });
        fireTrackAction({
          subactionName: TrackingConstant.AVAILABLE_OPTIONS_COUNT,
          value: optionsLength,
        });
        fireTrackAction({
          subactionName: TrackingConstant.AVAILABLE_PACKAGE_OPTIONS_COUNT,
          value: packagesToDisplay.length,
        });
      }

      const availableLowValueOptionsCount = optionsToDisplay.filter(isLowValueOption).length;

      if (availableLowValueOptionsCount) {
        fireTrackAction({
          subactionName: SUBACTION_NAMES.AVAILABLE_LOW_VALUE_OPTIONS_COUNT,
          value: availableLowValueOptionsCount,
        });
      }
    }
  }, [vehicleFeaturesId, isStepActive]);

  // This logic is also used in the style mismatch modal
  // In order for it to not fire twice, enable it only when the step is active (inside modal)
  // Or on the first step is active (when style can be changed)
  useEffect(() => {
    if (vehicleFeaturesId && (isStyleMismatchModal || isLocationStepActive || isStepActive)) {
      const nextTransmissionType =
        !vehicleFeaturesTransmissionType && !isEmpty(vehicleFeatures)
          ? UNDEFINED_STR_VALUE
          : vehicleFeaturesTransmissionType;

      const equipment = vehicleFeaturesOptions.map(option => option.name);
      updateStepsState({
        partnerEquipment: equipment,
      });

      const chromeStyleIds = partnerMapping?.chromeIds ?? [];
      setChromeStyleIds(chromeStyleIds);
      fireTrackAction({
        subactionName: SUBACTION_NAMES.STYLE_CHROME_STYLE_ID_COUNT,
        value: chromeStyleIds.length,
      });
      fireTrackAction({ subactionName: SUBACTION_NAMES.STYLE_CHROME_STYLE_ID_PASSED, value: chromeStyleIds[0] });

      if (isEmpty(prefilledOptions)) {
        setOptions(vehicleFeaturesOptions);
      }

      setTransmissionType(nextTransmissionType);

      if (isExtendedReport) {
        updateEngineAndTransmissionType({ isTransmissionSelectedByUser: false });
      }

      fireTrackAction({ subactionName: SUBACTION_NAMES.STYLE_ENGINE_SIZE, value: vehicleFeaturesEngine?.size });
      fireTrackAction({ subactionName: SUBACTION_NAMES.STYLE_TRANSMISSION_TYPE, value: nextTransmissionType });
      fireTrackAction({
        subactionName: SUBACTION_NAMES.STYLE_DRIVETRAIN,
        value: vehicleFeaturesDriveTrain,
      });
    }
  }, [vehicleFeaturesId]);

  useEffect(() => {
    if (!isStyleMismatchModal && !isEqual(multiOfferPrequalification, prevMultiOfferPrequalification)) {
      fireMultiOfferPrequalificationTracking(multiOfferPrequalification);
    }
  }, [isStyleMismatchModal, multiOfferPrequalification, prevMultiOfferPrequalification]);

  const handleColorSelect = useCallback(
    (color, isStepDone, isExterior) => {
      const colorList = isExterior ? exteriorColors : interiorColors;
      const selectedColorObj = findColorByName(colorList, color);

      if (selectedColorObj) {
        const isStepDoneValue = isStyleMismatchModal ? false : isStepDone;
        delayHeavyTask(() => {
          if (isExterior) {
            onExteriorColorSelect(selectedColorObj, isStepDoneValue);
          } else {
            onInteriorColorSelect(selectedColorObj, isStepDoneValue);
          }
        });
      }
    },
    [exteriorColors, interiorColors, isStyleMismatchModal, onExteriorColorSelect, onInteriorColorSelect]
  );

  const handleExteriorColorSelect = useCallback(
    (color, isStepDone = true) => {
      handleColorSelect(color, isStepDone, true);
    },
    [handleColorSelect]
  );

  const handleInteriorColorSelect = useCallback(
    (color, isStepDone = true) => {
      handleColorSelect(color, isStepDone, false);
    },
    [handleColorSelect]
  );

  useEffect(() => {
    if (isExtendedReport && engineOrTransmissionChanged) {
      updateEngineAndTransmissionType({ isTransmissionSelectedByUser: false });
    }
  }, [engineOrTransmissionChanged, isExtendedReport, updateEngineAndTransmissionType]);

  useEffect(() => {
    if (transmissionTypeFromLastQuote && isStepBecameActive && isNotAvailableTransmissionType) {
      setTransmissionType(transmissionTypeFromLastQuote);

      setTranslationTransmissionType({
        transmissionType: transmissionTypeFromLastQuote,
        isOverride: false,
        isSelectedByUser: true,
      });
    }
  }, [
    isNotAvailableTransmissionType,
    isStepBecameActive,
    setTranslationTransmissionType,
    transmissionType,
    transmissionTypeFromLastQuote,
  ]);

  useEffect(() => {
    if (prefilledExteriorColorId && isEmpty(exteriorColor)) {
      const prefilledExteriorColorName = exteriorColors.find(
        ({ id }) => `${id}` === `${prefilledExteriorColorId}`
      )?.name;
      if (prefilledExteriorColorName) {
        handleExteriorColorSelect(prefilledExteriorColorName, false);
      }
    }
  }, [handleExteriorColorSelect, prefilledExteriorColorId, exteriorColors]);

  useEffect(() => {
    if (prefilledInteriorColorId && isEmpty(interiorColor)) {
      const prefilledInteriorColorName = interiorColors.find(
        ({ id }) => `${id}` === `${prefilledInteriorColorId}`
      )?.name;
      if (prefilledInteriorColorName) {
        handleInteriorColorSelect(prefilledInteriorColorName, false, false);
      }
    }
  }, [handleInteriorColorSelect, prefilledInteriorColorId, interiorColors]);

  const prevPrefilledOptions = usePrevious(prefilledOptions);

  useEffect(() => {
    if (
      isEmpty(selectedOptions) &&
      !isEmpty(prefilledOptions) &&
      !isEmpty(vehicleFeaturesOptions) &&
      (!isOptionsEqual(prefilledOptions, prevPrefilledOptions) ||
        hasVehicleFeaturesChanged ||
        (isStyleMismatchModal && isStepBecameActive))
    ) {
      const nextOptions = vehicleFeaturesOptions.map(item => {
        const isPreSelected = !!prefilledOptions.find(
          ({ description, name }) => description === item.name || name === item.name
        );
        const isDisplayed = !!getOptionsToDisplay(vehicleFeaturesOptions, isExtendedReport, showOptions).find(
          ({ name }) => name === item.name
        );
        if (isDisplayed && isPreSelected) {
          return {
            ...item,
            selected: true,
          };
        }
        return item;
      });
      setOptions(nextOptions);
      const selectedNextOptions = getSelectedOptions(nextOptions);
      const optionsCount = selectedNextOptions.length;

      delayHeavyTask(() => {
        onOptionsSelect(selectedNextOptions, optionsCount, false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVehicleFeaturesChanged, prefilledOptions, isStyleMismatchModal, isStepBecameActive]);

  const onTransmissionTypeChange = useCallback(
    selectedTransmissionType => {
      setTransmissionType(selectedTransmissionType);
      updateEngineAndTransmissionType({
        isTransmissionSelectedByUser: true,
        newTransmissionType: selectedTransmissionType,
      });
    },
    [updateEngineAndTransmissionType]
  );

  const handleOptionsSelect = useCallback(
    (option, selected) => {
      const optionsCopy = cloneDeep(options);
      const selectedIndex = options.findIndex(({ id }) => id === option.id);
      optionsCopy[selectedIndex].selected = selected;
      const isEngineOrTransmissionOptionSelected = isEngineOrTransmissionOption(optionsCopy?.[selectedIndex] ?? {});
      setOptions(optionsCopy);
      setEngineOrTransmissionChanged(isEngineOrTransmissionOptionSelected);

      const isStepDoneValue = !isStyleMismatchModal;

      delayHeavyTask(() => {
        onOptionsSelect(getSelectedOptions(optionsCopy), optionsCopy.length, isStepDoneValue);
      });
    },
    [isStyleMismatchModal, onOptionsSelect, options]
  );

  const handlePackageSelect = useCallback(
    (pkg, selected) => {
      const optionsCopy = cloneDeep(options);

      if (selected) {
        const featureIds = new Set(pkg.genericFeatures?.map(({ id }) => id) ?? []);
        optionsCopy.forEach(opt => {
          if (featureIds.has(opt.id)) {
            opt.selected = true; // eslint-disable-line no-param-reassign
          }
        });
      } else {
        const idsToDeselect = getPackageDeselectionIds(
          pkg,
          options,
          featureToPackages,
          pkgByOemName,
          packagesToDisplay
        );
        optionsCopy.forEach(opt => {
          if (idsToDeselect.has(opt.id)) {
            opt.selected = false; // eslint-disable-line no-param-reassign
          }
        });
      }

      const isStepDoneValue = !isStyleMismatchModal;
      setOptions(optionsCopy);
      delayHeavyTask(() => {
        onOptionsSelect(getSelectedOptions(optionsCopy), optionsCopy.length, isStepDoneValue);
      });
    },
    [featureToPackages, isStyleMismatchModal, onOptionsSelect, options, packagesToDisplay, pkgByOemName]
  );

  if (!isStepActive && !isStyleMismatchModal) return null;

  const hasOptions = !!optionsLength && showOptions;
  const hasExteriorColors = !!size(exteriorColors);
  const hasInteriorColors = !!size(interiorColors);
  const isBigExteriorColorMessage = !hasExteriorColors && !hasInteriorColors && !hasOptions;
  const CategoryTitle = isExtendedReport ? 'h3' : 'h4';

  return (
    <div
      className={classnames('colors-and-options-step text-gray-darker', {
        'style-mismatch-modal': isStyleMismatchModal,
      })}
    >
      {showColors && (
        <Fragment>
          <ColorsSection
            colors={exteriorColors}
            selectedColor={exteriorColor?.name ? { [exteriorColor?.name]: true } : {}}
            onColorSelect={handleExteriorColorSelect}
            sectionName="Exterior Color"
            sectionKey={EXTERIOR_COLOR}
            errorMap={errorMap}
            className={{
              'mb-1':
                (hasExteriorColors && (!isExtendedReport || !hasOptions)) ||
                (!hasExteriorColors && isExtendedReport && isNotAvailableTransmissionType),
              'mb-1_5 border-bottom': isExtendedReport && (hasOptions || isNotAvailableTransmissionType),
              'display-3': isBigExteriorColorMessage,
            }}
            titleClassName={{
              'category w-100 bg-blue-100 fw-bold text-cool-gray-30 px-1 py-0_5 mb-1_5 rounded': isStyleMismatchModal,
              'mb-2': isBigExteriorColorMessage,
              'mb-0_5': !isBigExteriorColorMessage,
            }}
            isExtendedReport={isExtendedReport}
          />
          {hasInteriorColors && (
            <ColorsSection
              colors={interiorColors}
              selectedColor={interiorColor?.name ? { [interiorColor?.name]: true } : {}}
              onColorSelect={handleInteriorColorSelect}
              sectionName="Interior Color"
              errorMap={errorMap}
              isExtendedReport={isExtendedReport}
              titleClassName={{
                'category w-100 bg-blue-100 fw-bold text-cool-gray-30 px-1 py-0_5 mb-1_5 rounded': isStyleMismatchModal,
              }}
              className={{
                'mb-1': !isExtendedReport || !hasOptions,
                'mb-1_5 border-bottom': isExtendedReport && (hasOptions || isNotAvailableTransmissionType),
              }}
            />
          )}
        </Fragment>
      )}
      {packagesToDisplay.length > 0 && showOptions && (
        <fieldset className="mb-1">
          <legend className="mb-0_5">
            <CategoryTitle className="size-16 d-inline mb-0 fw-bold">Equipment Packages</CategoryTitle>
          </legend>
          <p className="size-14 text-gray-darker mb-1">
            Packages are bundled groups of optional features added by the manufacturer. Selecting one automatically
            checks all features associated with that bundle.
          </p>
          <FacetCheckbox
            onUpdate={handlePackageSelect}
            values={packagesToDisplay.map(pkg => ({
              ...pkg,
              name: pkg.oemName,
              selected: isPackageSelected(pkg, options),
            }))}
            facetName="packages"
            listClasses="options-list p-0 pe-md-0_5"
            checkboxClasses="medium"
            labelClasses="align-items-baseline"
            visibleValues={isMobile || isStyleMismatchModal ? 6 : packagesToDisplay.length}
            collapseText={{ less: 'See less', more: 'See more' }}
            collapseClasses="text-primary-darker ps-1_25"
            showCollapseIcon
            normalSize
            disableTracking
            hideCount
            isContrastUpdate={isContrastUpdate}
          />
        </fieldset>
      )}
      {hasOptions && (
        <fieldset
          className={classnames({
            'mb-1_5 border-bottom': isExtendedReport && isNotAvailableTransmissionType,
          })}
        >
          <legend className="mb-1">
            <CategoryTitle className="size-16 d-inline mb-0 fw-bold">Additional Equipment:</CategoryTitle>
          </legend>
          <div
            className={classnames('options-wrapper', {
              'mb-1': isExtendedReport && isNotAvailableTransmissionType,
            })}
          >
            <FacetCheckbox
              onUpdate={handleOptionsSelect}
              values={optionsToDisplay}
              facetName="options"
              listClasses="options-list p-0 pe-md-0_5"
              checkboxClasses="medium"
              labelClasses="align-items-baseline"
              visibleValues={isMobile || isStyleMismatchModal ? 6 : optionsToDisplay.length}
              collapseText={{ less: 'See less', more: 'See more' }}
              collapseClasses="text-primary-darker ps-1_25"
              showCollapseIcon
              normalSize
              disableTracking
              hideCount
              isContrastUpdate={isContrastUpdate}
            />
          </div>
        </fieldset>
      )}
      {isExtendedReport && isNotAvailableTransmissionType && (
        <TransmissionTypeSelection
          selectedTransmissionType={transmissionType}
          onTransmissionTypeChange={onTransmissionTypeChange}
          errorMap={errorMap}
        />
      )}
    </div>
  );
};

ColorsAndOptionsStepUI.propTypes = {
  isStepActive: PropTypes.bool.isRequired,
  vehicleFeatures: VehicleEntities.VehicleFeatures,
  isExtendedReport: PropTypes.bool,
  isMobile: PropTypes.bool,
  errorMap: PropTypes.objectOf(PropTypes.string),
  vehicle: PropTypes.shape({}),
  drawerCreativeId: PropTypes.string,
  transmissionType: PropTypes.string,
  isContrastUpdate: PropTypes.bool,
  prefilledExteriorColorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  prefilledInteriorColorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  prefilledOptions: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      oemCode: PropTypes.string,
    })
  ),
  multiOfferPrequalification: PropTypes.shape({}),
  showColors: PropTypes.bool,
  showOptions: PropTypes.bool,
  isStyleMismatchModal: PropTypes.bool,
  isLocationStepActive: PropTypes.bool,
  onColorsAndOptionsLoaded: PropTypes.func,
  vinStyles: PropTypes.shape({}),
  isStepDone: PropTypes.bool,
  isLoadingColorsAndOptions: PropTypes.bool,
  exteriorColor: VehicleEntities.Color,
  interiorColor: VehicleEntities.Color,
};

export const mapStateToProps = state => ({
  zipCode: state?.visitor?.location?.zipCode,
  location: state?.pageContext?.location,
});

export const stateToPropsConfig = {
  vehicleFeatures: bindToPath(({ vehicle }) => {
    const styleId = vehicle?.style?.id;
    return transformedVehicleFeaturesPath.build(styleId);
  }, PartnerOfferModel),
  vinStyles: bindToPath(({ vin }) => buildStylesBasicPathFromVin({ vin }), VehicleVinModel),
};

export const ColorsAndOptionsStep = connect(mapStateToProps)(
  connectToModel(ColorsAndOptionsStepUI, stateToPropsConfig)
);
